import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import T from 'i18n-react';
import { withSnackbar } from 'notistack';

import Grid from '@material-ui/core/Grid';
import withStyles from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';

import { API_V1_APPLICANTS, API_USER_APPLICABLE_TERMS } from '../routing';
import { AppDialog } from '../../core/Dialog/AppDialog';
import { getErrorMessageByStatus } from '../../core/ajax';
import Select from '../../core/FormField/Select';
import { CircularContainer } from '../../core/Shared/Progress';
import SurveyDialog from '../../core/Enquete/Survey/SurveyDialog';
import DialogWithClose from '../../core/Dialog/DialogWithClose';
import { getTermText } from '../../core/Plans/utils';
import ServiceAgreementCheckbox from './components/ServiceAgreementCheckbox';
import CheckboxControl from './components/CheckboxControl';
import WorkingNoticeLink from './components/WorkingNoticeLink';
import TermAmountText from './components/TermAmountText';
import NoApplicableTerm from './components/NoApplicableTerm';
import YoungRestriction from './components/YoungRestriction';
import CustomGridItem from './components/CustomGridItem';
import MemoHelperText from './New/components/MemoHelperText';
import MultilineTextField from './New/containers/MultilineTextField';

const Circular = withStyles((theme) => ({
  root: {
    position: 'absolute',
    backgroundColor: theme.palette.common.white,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
}))(({ classes }) => (
  <CircularContainer containerProps={{ className: classes.root }} />
));

const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
  margin-top: 72px;
`;

const styles = (theme) => ({
  dialogPadding: {
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2, 4),
    },
  },
  dialogContent: {
    padding: theme.spacing(0, 2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(0, 4),
    },
  },
  workingNoticeDialogPaper: {
    width: `calc(100% - ${theme.spacing(1)}px)`,
    height: `calc(100% - ${theme.spacing(1)}px)`,
    margin: theme.spacing(1),
  },
});

const MOTIVE_MINIMUM_COUNT = 100;

class NewDialog extends SurveyDialog {
  constructor(props) {
    super(props);

    this.state = {
      ...this.getInitialState(),
      ...this.state,
      isYoung: false,
      terms: [],
      loaded: false,
    };

    this.clickWorkingNoticeLink = this.clickWorkingNoticeLink.bind(this);
  }

  loading = false;

  getInitialState = () => ({
    application: {
      confirm: false,
      approval: false,
      follow: true,
      termId: null,
      motive: '',
      memo: '',
      filled: false,
      isAgreedWorkingNotice: false,
      isInterviewAccepted: false,
      didUnderstandOtetsutabi: false,
    },
    validMotive: false,
    validMemo: true,
    complete: false,
    applicantId: null,
    isWatchedWorkingNotice: false,
    openWorkingNotice: false,
  });

  getTitle = () => {
    const { plan } = this.props;
    return (
      <Grid container wrap="nowrap" spacing={1}>
        <Grid item xs="auto" className="line-truncate-1">
          {plan}
        </Grid>
        <CustomGridItem margin="zero" xs="auto" textNoWrap>
          {T.translate('applicant.new.applyTo')}
        </CustomGridItem>
      </Grid>
    );
  };

  getDialogProps = () => ({
    id: 'applicationDialog',
    disableBackdropClick: true,
    keepMounted: true,
    maxWidth: 800,
    PaperProps: { style: { height: '100%' } },
  });

  getActionButtonText = () =>
    T.translate(this.state.complete ? 'survey.answer' : 'apply');

  getDialogTitleProps = () => {
    const { classes } = this.props;
    return {
      className: classes.dialogPadding,
    };
  };

  getDialogContentProps = () => {
    const { classes } = this.props;
    const { loaded } = this.state;
    return {
      ...(!loaded && { style: { overflow: 'hidden' } }),
      className: classes.dialogContent,
      ref: this.contentRootRef,
    };
  };

  getDialogActionsProps = () => {
    const { classes } = this.props;
    return {
      className: classes.dialogPadding,
    };
  };

  componentDidMount() {
    super.componentDidMount();

    const params = new URLSearchParams(window.location.search);
    if (params.get('a') === 'open') {
      this.handleOpen();
    }
  }

  handleOpen(event) {
    const { planId, enqueueSnackbar } = this.props;
    if (!this.loading && !this.state.loaded) {
      this.loading = true;
      this.fetchData(
        API_USER_APPLICABLE_TERMS.replace(':planId', planId),
        null,
        (data) => {
          const { redirect_to, reason, terms } = data;
          this.loading = false;
          const nextState = {
            loaded: true,
            open: true,
            terms: terms.map(
              ({
                id,
                start_date,
                end_date,
                working_notice_url,
                total_wages,
              }) => ({
                id,
                startDate: start_date,
                endDate: end_date,
                workingNoticeUrl: working_notice_url,
                totalWages: total_wages,
              }),
            ),
          };
          if (reason === 'age') {
            Object.assign(nextState, { isYoung: true });
          } else if (reason === 'mypage') {
            window.location.href = redirect_to;
            return;
          }

          this.setState(nextState);
        },
        (response, data) => {
          this.loading = false;
          const message = getErrorMessageByStatus(
            response,
            data,
            'new-applicant-error',
          );
          const messages = Array.isArray(message) ? message : [message];
          messages.forEach((m) => enqueueSnackbar(m, { variant: 'error' }));
        },
      );
    } else {
      super.handleOpen(event);
    }
  }

  handleClose(event) {
    // When closing dialog with the survey unanswered
    const callback = this.state.complete
      ? () => this.props.onSuccess?.({ terms: this.state.terms })
      : () => {};

    this.setState(
      (prevState) => ({
        open: false,
        ...(prevState.complete
          ? this.getInitialState()
          : {
              application: {
                ...prevState.application,
                filled: false,
                confirm: false,
                approval: false,
              },
            }),
      }),
      callback,
    );
  }

  handleChangeMotive = (value, valid) => {
    this.setState((prevState) => ({
      application: {
        ...prevState.application,
        motive: value,
      },
      validMotive: valid,
    }));
  };

  handleChangeMemo = (value, valid) => {
    this.setState((prevState) => ({
      application: {
        ...prevState.application,
        memo: value,
      },
      validMemo: valid,
    }));
  };

  handleClickMemoTemplate = () => {
    this.setState((prevState) => {
      const memo = prevState.application.memo;
      const parts = memo ? [memo.trimEnd(), ''] : [];
      parts.push(
        T.translate('applicant.new.together.template1'),
        T.translate('applicant.new.together.template2'),
        '',
        T.translate('applicant.new.together.template3'),
        T.translate('applicant.new.together.template4'),
        T.translate('applicant.new.together.template5'),
      );

      return {
        application: {
          ...prevState.application,
          memo: parts.join('\n'),
        },
      };
    });
  };

  isValidApplication = () => {
    const {
      application: {
        confirm,
        approval,
        isAgreedWorkingNotice,
        didUnderstandOtetsutabi,
      } = {},
      validMotive,
      validMemo,
    } = this.state;
    const term = this.getTerm();
    return (
      !!term &&
      validMotive &&
      validMemo &&
      didUnderstandOtetsutabi &&
      confirm &&
      (term.workingNoticeUrl ? isAgreedWorkingNotice : approval) // 雇用は労働条件通知書に同意、業務委託は金額変動の同意
    );
  };

  isActionButtonDisabled(): boolean {
    return !this.isValidApplication() || super.isActionButtonDisabled();
  }

  handleChange =
    (key, checkbox = false) =>
    (event) =>
      this.setState((prevState) => ({
        application: {
          ...prevState.application,
          [key]: event.target[checkbox ? 'checked' : 'value'],
        },
      }));

  mainContentRenderer = () => {
    const { loaded } = this.state;
    return (
      <>
        {this.getContent()}
        {!loaded && <Circular />}
      </>
    );
  };

  getTerm = () => {
    const {
      terms,
      application: { termId },
    } = this.state;
    return terms.find(({ id }) => id === termId);
  };

  clickWorkingNoticeLink = () => {
    this.setState({ isWatchedWorkingNotice: true, openWorkingNotice: true });
  };

  handleCloseWorkingNotice = () => {
    this.setState({ openWorkingNotice: false });
  };

  getContent = () => {
    const { classes, businessName, otherContent } = this.props;
    const {
      application: {
        termId,
        motive,
        memo,
        follow,
        confirm,
        approval,
        isAgreedWorkingNotice,
        isInterviewAccepted,
        didUnderstandOtetsutabi,
      } = {},
      terms = [],
      isYoung,
      isWatchedWorkingNotice,
      openWorkingNotice,
    } = this.state;

    if (isYoung) {
      return <YoungRestriction />;
    }
    if (terms.length === 0) {
      return <NoApplicableTerm />;
    }

    const term = this.getTerm();
    const isTermSelected = term != null;
    const isEmployment = isTermSelected && !!term.workingNoticeUrl;
    const isOutsourcing = isTermSelected && term.workingNoticeUrl == null;

    return (
      <Grid container>
        <CustomGridItem>
          <Select
            fullWidth
            required
            includeBlank
            blankText="-"
            value={termId}
            FormControlProps={{ required: true }}
            choices={terms.map((t) => ({
              text: getTermText({ startDate: t.startDate, endDate: t.endDate }),
              value: t.id,
            }))}
            label={T.translate('applicant.new.term')}
            InputLabelProps={{ classes: { root: classes.inputLabel } }}
            MenuItemProps={{ style: { whiteSpace: 'normal' } }}
            onChange={this.handleChange('termId')}
          />
        </CustomGridItem>
        {isEmployment && (
          <>
            <CustomGridItem>
              <WorkingNoticeLink
                startDate={term.startDate}
                endDate={term.endDate}
                onClick={this.clickWorkingNoticeLink}
              />
              &nbsp;
              <Typography component="span">
                {T.translate('applicant.new.clickAndConfirmWorkingNotice')}
              </Typography>
            </CustomGridItem>
            <DialogWithClose
              fullWidth
              open={openWorkingNotice}
              onClickClear={this.handleCloseWorkingNotice}
              onBackdropClick={this.handleCloseWorkingNotice}
              classes={{ paper: classes.workingNoticeDialogPaper }}
            >
              <Iframe
                title={T.translate('applicant.new.workingNotice')}
                // ナビゲーションペインおよびタブを非表示にすることで表示領域を確保する
                src={`${term.workingNoticeUrl}#navpanes=0`}
              />
            </DialogWithClose>
          </>
        )}
        {/* このおてつたび先に行きたい理由 */}
        <CustomGridItem margin="dense">
          <MultilineTextField
            required
            value={motive}
            placeholder={T.translate('applicant.new.motiveHelperText', {
              count: MOTIVE_MINIMUM_COUNT,
            })}
            maxLength={500}
            minLength={MOTIVE_MINIMUM_COUNT}
            rows={10}
            label={T.translate('applicant.new.reason')}
            htmlFor="applicantMotive"
            onChange={this.handleChangeMotive}
          />
        </CustomGridItem>
        {/* その他、連絡事項 */}
        <CustomGridItem margin="dense">
          <MultilineTextField
            value={memo}
            maxLength={255}
            placeholder={T.translate('applicant.new.memoPlaceholder')}
            rows={8}
            label={T.translate('applicant.new.memo')}
            htmlFor="applicantMemo"
            onChange={this.handleChangeMemo}
            template={{
              text: T.translate('applicant.new.together.apply'),
              onClick: this.handleClickMemoTemplate,
            }}
          />
          <MemoHelperText />
        </CustomGridItem>
        <CustomGridItem margin="zero">
          <CheckboxControl
            label={T.translate('applicant.new.follow', {
              name: `${businessName.slice(0, 20)}${
                businessName.length > 20 ? '...' : ''
              }`,
            })}
            checked={follow}
            onChange={this.handleChange('follow', true)}
            helpText={T.translate('applicant.new.followHelper')}
          />
        </CustomGridItem>
        <CustomGridItem>
          <CheckboxControl
            label={T.translate('applicant.new.isInterviewAccepted')}
            checked={isInterviewAccepted}
            onChange={this.handleChange('isInterviewAccepted', true)}
            helpText={T.translate('applicant.new.isInterviewAcceptedHelper')}
          />
        </CustomGridItem>
        <CustomGridItem>
          <Typography>
            <strong>
              {T.translate('applicant.new.requiredCheckDescription')}
            </strong>
          </Typography>
        </CustomGridItem>
        {isEmployment && (
          <>
            <CustomGridItem>
              <CheckboxControl
                label={T.translate('applicant.new.consentedForWorkingNotice', {
                  notice: (
                    <WorkingNoticeLink onClick={this.clickWorkingNoticeLink} />
                  ),
                })}
                checked={isAgreedWorkingNotice}
                disabled={!isWatchedWorkingNotice}
                onChange={this.handleChange('isAgreedWorkingNotice', true)}
                helpText={T.translate('applicant.new.workingNoticeCheckHint')}
              />
            </CustomGridItem>
          </>
        )}
        {isOutsourcing && (
          <CustomGridItem>
            <CheckboxControl
              label={T.translate('applicant.new.approval')}
              checked={approval}
              onChange={this.handleChange('approval', true)}
            />
            <TermAmountText
              startDate={term.startDate}
              endDate={term.endDate}
              totalWages={term.totalWages}
            />
          </CustomGridItem>
        )}
        <CustomGridItem margin="zero">
          <CheckboxControl
            label={T.translate('applicant.new.understandOtetsutabi')}
            checked={didUnderstandOtetsutabi}
            onChange={this.handleChange('didUnderstandOtetsutabi', true)}
          />
        </CustomGridItem>
        <CustomGridItem margin="zero">
          <ServiceAgreementCheckbox
            checked={confirm}
            onChange={this.handleChange('confirm', true)}
          />
        </CustomGridItem>
        {!!otherContent && (
          <CustomGridItem margin="zero">{otherContent}</CustomGridItem>
        )}
      </Grid>
    );
  };

  handleAccept(event) {
    const {
      application: {
        termId,
        motive,
        memo,
        follow,
        isAgreedWorkingNotice,
        isInterviewAccepted,
      } = {},
    } = this.state;

    this.setState({ progress: true });

    this.fetchData(
      API_V1_APPLICANTS,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          termId,
          motive,
          memo,
          follow,
          isAgreedWorkingNotice,
          isInterviewAccepted,
        }),
      },
      ({ id: applicantId, name, questionnaireUrl, acceptingTerms = [] }) => {
        this.setState({
          complete: true,
          progress: false,
          applicantId,
          name,
          questionnaireUrl,
          terms: acceptingTerms,
        });
      },
      this.handleErrorResponse,
    );
  }

  // 継承元のAppDialogのhandleErrorResponseをoverriderしている
  handleErrorResponse = (response, data) => {
    data.errors.forEach((error) => {
      this.handleError({ message: error.errorMessage, severity: 'error' });
    });
  };
}

NewDialog.propTypes = {
  ...AppDialog.propTypes,
  plan: PropTypes.string,
  planId: PropTypes.any,
  businessName: PropTypes.string,
  otherContent: PropTypes.node,
};

export default withSnackbar(withStyles(styles)(NewDialog));
